import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ExpirationDateComponent } from "./expirationDateComponent";

export const SealedBrokenComponent = React.forwardRef((props, ref) => {
  const { name, defaultValue, onChange, data } = props;
  const options = data.options || [];

  // Helper to parse formatted date (e.g., "Jan 15th, 2025") to YYYY-MM-DD
  const parseFormattedDate = (dateStr) => {
    if (!dateStr) return "";
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    
    try {
      // Try to parse formatted date like "Jan 15th, 2025"
      const dateMatch = dateStr.match(/([A-Za-z]{3})\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/);
      if (dateMatch) {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthIndex = months.findIndex((m) => m === dateMatch[1]);
        const day = parseInt(dateMatch[2], 10);
        const year = parseInt(dateMatch[3], 10);
        if (monthIndex !== -1) {
          const date = new Date(year, monthIndex, day, 12, 0, 0);
          return date.toISOString().split("T")[0];
        }
      }
      // Try direct Date parsing as fallback
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }
    } catch (e) {
      console.error("Error parsing date:", e);
    }
    return "";
  };

  // Parse defaultValue which may be a string (from draft) or object
  const parseDefaultValue = (val) => {
    if (!val) return { label: "", options: [], expirationDates: {} };
    if (typeof val === "string") {
      try {
        // Try to parse as JSON first
        return JSON.parse(val);
      } catch (e) {
        // If JSON parse fails, check if it's a simple "sealed" or "broken" string
        if (val === "sealed" || val === "broken") {
          return { label: val, options: [], expirationDates: {} };
        }
        return { label: "", options: [], expirationDates: {} };
      }
    }
    return val;
  };
  
  const initialValue = parseDefaultValue(defaultValue) || parseDefaultValue(props.default) || { label: "", options: [], expirationDates: {} };
  
  // Initialize expiration dates from both draft data and option defaults
  // Priority: draft data (initialValue.expirationDates) > option defaults
  const getInitialExpirationDates = () => {
    const dates = { ...(initialValue.expirationDates || {}) };
    // Only add option defaults if draft doesn't have a value for that key
    if (options) {
      options.forEach((option) => {
        if (option.hasExpiration && option.expiration_date && !dates[option.key]) {
          dates[option.key] = parseFormattedDate(option.expiration_date);
        }
      });
    }
    return dates;
  };
  
  const [selectedOption, setSelectedOption] = useState({
    label: initialValue.label || "",
    options: initialValue.options || [],
    expirationDates: initialValue.expirationDates || {},
  });
  const [checkedOptions, setCheckedOptions] = useState(initialValue.options || []);
  const [manualExpirationDates, setManualExpirationDates] = useState(getInitialExpirationDates);
  
  // State for overall expiration date (used when hasExpiration is true - Quick Check items)
  const [overallExpiration, setOverallExpiration] = useState(
    parseFormattedDate(data?.expiration_date) || parseFormattedDate(data?.overallExpiration) || parseFormattedDate(initialValue.overallExpiration) || ""
  );

  const sealedId = `passOption_${name}_sealed`;
  const brokenId = `failOption_${name}_broken`;

  const sealedRef = useRef(null);
  const brokenRef = useRef(null);

  useLayoutEffect(() => {
    if (ref && (props.default === "sealed" || props.default === "broken")) {
      ref.current = sealedRef.current;
    }
  }, []);

  useEffect(() => {
    if (ref && ref.current) {
      // Include expirationDates and overallExpiration in the stored value so they persist in drafts
      const valueToStore = {
        ...selectedOption,
        expirationDates: manualExpirationDates,
        overallExpiration: overallExpiration,
      };
      ref.current.value = JSON.stringify(valueToStore);
    }
  }, [selectedOption, manualExpirationDates, overallExpiration]);

  const handleExpirationDateChange = (optionKey, value) => {
    setManualExpirationDates((prev) => ({
      ...prev,
      [optionKey]: value,
    }));
  };

  const handleOptionChange = (event) => {
    console.log(ref);
    const value = event.target.value;
    ref.current = event.target;

    console.log(ref);
    setSelectedOption((prevCheckedOptions) => {
      return {
        ...prevCheckedOptions,
        label: value,
      };
    });
    setCheckedOptions([]);
  };

  const handleCheckboxChange = (event) => {
    const value = event.target.value;
    const checked = event.target.checked;
    setCheckedOptions((prevCheckedOptions) => {
      let updatedOptions;
      if (checked) {
        updatedOptions = [...prevCheckedOptions, value];
      } else {
        updatedOptions = prevCheckedOptions.filter(
          (option) => option !== value
        );
      }

      return updatedOptions;
    });

    setSelectedOption((prevCheckedOptions) => {
      let updatedOptions;
      if (checked) {
        updatedOptions = [...checkedOptions, value];
      } else {
        updatedOptions = checkedOptions.filter((option) => option !== value);
      }

      return {
        ...prevCheckedOptions,
        options: updatedOptions,
      };
    });
  };

  return (
    <div className="form-group sealed-broken">
      <div className="custom-control custom-radio-passfail option-inline">
        <input
          className="custom-control-input"
          name={name}
          type="radio"
          value="sealed"
          defaultValue={defaultValue}
          checked={selectedOption.label === "sealed"}
          onChange={handleOptionChange}
          id={sealedId}
          ref={sealedRef}
        />
        <label className="custom-control-label-passfail" htmlFor={sealedId}>
          Sealed
        </label>
      </div>
      <div className="custom-control custom-radio-passfail option-inline">
        <input
          className={"custom-control-input"}
          name={name}
          type="radio"
          value="broken"
          defaultValue={defaultValue}
          checked={selectedOption.label === "broken"}
          onChange={handleOptionChange}
          id={brokenId}
          ref={brokenRef}
        />
        <label className="custom-control-label-passfail" htmlFor={brokenId}>
          Broken
        </label>
      </div>
      {selectedOption.label === "broken" &&
        data.options &&
        data.showSealBrokenChecklistItems && (
          <div>
            {options.map((option) => (
              <div key={option.key} className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={`option_${option.key}`}
                  defaultChecked={checkedOptions.includes(option.value)}
                  onChange={handleCheckboxChange}
                  name={`${name}_options`}
                  value={option.value}
                />
                <label
                  className="custom-control-label"
                  htmlFor={`option_${option.key}`}
                >
                  {option.text}
                </label>
                {option.hasExpiration && (
                  <div className="expiration-date-container modern-date-input">
                    <div className="date-label">
                      {option.text} Expiration Date
                    </div>
                    <div className="date-input-wrapper form-group">
                      <input
                        type="date"
                        style={{ color: "black" }}
                        className="modern-date-control"
                        name={`${option.key}_expiration`}
                        onChange={(e) =>
                          handleExpirationDateChange(option.key, e.target.value)
                        }
                        value={manualExpirationDates[option.key] || ""}
                      />
                      <div className="date-icon">📅</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      {/* Single expiration date - show when hasExpiration is true (Quick Check items) */}
      {data.hasExpiration && (
        <div className="mt-3">
          <ExpirationDateComponent
            {...props}
            name={`${name}_expiration`}
            value={overallExpiration}
            defaultValue={overallExpiration}
            data={{
              ...data,
              label: "Overall Expiration Date",
              required: data.required
            }}
            onChange={(expirationName, expirationDate) => {
              setOverallExpiration(expirationDate);
            }}
          />
        </div>
      )}
    </div>
  );
});
